import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MapPin, Phone, Mail, Home, Navigation } from 'feather-icons-react/build/IconComponents';
import './deliveryaddress.css';

const DeliveryAddress = ({ show, onClose, onSubmit, initialData = null }) => {
  const [deliveryType, setDeliveryType] = useState('livrer'); // 'livrer' ou 'emporter'
  const [formData, setFormData] = useState({
    nom: '',
    telephone: '',
    email: '',
    adresse: '',
    complement: '',
    code_postal: '',
    ville: '',
    autre: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Récupérer les informations du restaurant depuis localStorage
  const getRestaurantInfo = () => {
    try {
      const restaurantData = JSON.parse(localStorage.getItem('restaurant'));
      return {
        nom: restaurantData?.restaurant?.nom || restaurantData?.Restaurant?.nom || '',
        telephone: restaurantData?.restaurant?.telephone || restaurantData?.Restaurant?.telephone || '',
        email: restaurantData?.restaurant?.email || restaurantData?.Restaurant?.email || 'contact@restaurant.com',
        adresse: restaurantData?.restaurant?.adresse || restaurantData?.Restaurant?.adresse || '',
        complement: restaurantData?.restaurant?.complement || restaurantData?.Restaurant?.complement || '',
        code_postal: restaurantData?.restaurant?.code_postal || restaurantData?.Restaurant?.code_postal || '',
        ville: restaurantData?.restaurant?.ville || restaurantData?.Restaurant?.ville || 'Non renseignée',
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des infos restaurant:', error);
      return null;
    }
  };

  // Populate form with initial data if editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        nom: initialData.nom || '',
        telephone: initialData.telephone || '',
        email: initialData.email || '',
        adresse: initialData.adresse || '',
        complement: initialData.complement || '',
        code_postal: initialData.code_postal || '',
        ville: initialData.ville || '',
        autre: initialData.autre || ''
      });
      // Détecter le type basé sur les données
      setDeliveryType(initialData.deliveryType || 'livrer');
    } else if (show) {
      // Reset au type "livrer" par défaut à l'ouverture
      setDeliveryType('livrer');
    }
  }, [initialData, show]);

  // Remplir automatiquement avec les infos du restaurant quand "À emporter" est sélectionné
  useEffect(() => {
    if (deliveryType === 'emporter') {
      const restaurantInfo = getRestaurantInfo();
      if (restaurantInfo) {
        setFormData({
          nom: restaurantInfo.nom,
          telephone: restaurantInfo.telephone,
          email: restaurantInfo.email,
          adresse: restaurantInfo.adresse,
          complement: restaurantInfo.complement || '',
          code_postal: restaurantInfo.code_postal || '',
          ville: restaurantInfo.ville,
          autre: 'Commande à emporter au restaurant'
        });
        setErrors({});
      }
    } else if (!initialData) {
      // Reset form si on revient à "À livrer" et qu'il n'y a pas de données initiales
      setFormData({
        nom: '',
        telephone: '',
        email: '',
        adresse: '',
        complement: '',
        code_postal: '',
        ville: '',
        autre: ''
      });
    }
  }, [deliveryType, initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Nom required
    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom est requis';
    }

    // Téléphone required and format
    const telephoneStr = String(formData.telephone || '').trim();
    if (!telephoneStr) {
      newErrors.telephone = 'Le téléphone est requis';
    } else if (!/^\d{9,15}$/.test(telephoneStr.replace(/\s/g, ''))) {
      newErrors.telephone = 'Numéro de téléphone invalide';
    }

    // Email required and format
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }

    // Adresse required
    if (!formData.adresse.trim()) {
      newErrors.adresse = 'L\'adresse est requise';
    }

    // Ville required
    if (!formData.ville.trim()) {
      newErrors.ville = 'La ville est requise';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Prepare data with proper formatting
      const telephoneStr = String(formData.telephone || '').replace(/\s/g, '');
      const dataToSubmit = {
        ...formData,
        telephone: parseInt(telephoneStr) || 0,
        deliveryType // Inclure le type de livraison
      };

      await onSubmit(dataToSubmit);
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      nom: '',
      telephone: '',
      email: '',
      adresse: '',
      complement: '',
      code_postal: '',
      ville: '',
      autre: ''
    });
    setErrors({});
  };

  if (!show) return null;

  return (
    <>
      {/* Modal Backdrop */}
      <div 
        className="delivery-modal-backdrop"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="delivery-modal">
        <div className="delivery-modal-content">
          {/* Header */}
          <div className="delivery-modal-header">
            <div className="delivery-modal-header-content">
              <MapPin size={24} className="delivery-modal-icon" />
              <h3 className="delivery-modal-title">Adresse de livraison</h3>
            </div>
            <button 
              type="button"
              className="delivery-modal-close"
              onClick={onClose}
              aria-label="Fermer"
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="delivery-modal-body">
            {/* Section Type de livraison */}
            <div className="delivery-type-section">
              <label className="delivery-form-label" style={{ marginBottom: '12px' }}>
                Type de commande <span className="delivery-required">*</span>
              </label>
              <div className="delivery-type-options">
                <label className="delivery-type-option">
                  <input
                    type="radio"
                    name="deliveryType"
                    value="livrer"
                    checked={deliveryType === 'livrer'}
                    onChange={(e) => setDeliveryType(e.target.value)}
                    className="delivery-radio-input"
                  />
                  <span className="delivery-radio-label">
                    <Navigation size={18} style={{ marginRight: '8px' }} />
                    À livrer
                  </span>
                </label>
                
                <label className="delivery-type-option">
                  <input
                    type="radio"
                    name="deliveryType"
                    value="emporter"
                    checked={deliveryType === 'emporter'}
                    onChange={(e) => setDeliveryType(e.target.value)}
                    className="delivery-radio-input"
                  />
                  <span className="delivery-radio-label">
                    <Home size={18} style={{ marginRight: '8px' }} />
                    À emporter
                  </span>
                </label>
              </div>
            </div>

            {/* Message informatif pour "À emporter" */}
            {deliveryType === 'emporter' && (
              <div className="delivery-info-banner">
                <MapPin size={20} />
                <div>
                  <strong>Point de retrait :</strong> Vous retirerez votre commande directement au restaurant à l&apos;adresse suivante : <strong>{formData.adresse}, {formData.ville}</strong>
                </div>
              </div>
            )}

            <p className="delivery-modal-description">
              {deliveryType === 'livrer' 
                ? 'Veuillez renseigner votre adresse de livraison pour finaliser votre commande.'
                : 'Les informations du restaurant sont pré-remplies. Vous pouvez ajouter des notes complémentaires si nécessaire.'
              }
            </p>

            {/* Nom complet */}
            <div className="delivery-form-group">
              <label className="delivery-form-label">
                <Home size={16} />
                Nom complet <span className="delivery-required">*</span>
              </label>
              <input
                type="text"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                disabled={deliveryType === 'emporter'}
                className={`delivery-form-input ${errors.nom ? 'delivery-form-input-error' : ''}`}
                placeholder="Ex: Jean Dupont"
                style={deliveryType === 'emporter' ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
              />
              {errors.nom && <span className="delivery-form-error">{errors.nom}</span>}
            </div>

            {/* Téléphone */}
            <div className="delivery-form-group">
              <label className="delivery-form-label">
                <Phone size={16} />
                Téléphone <span className="delivery-required">*</span>
              </label>
              <input
                type="tel"
                name="telephone"
                value={formData.telephone}
                onChange={handleChange}
                disabled={deliveryType === 'emporter'}
                className={`delivery-form-input ${errors.telephone ? 'delivery-form-input-error' : ''}`}
                placeholder="Ex: 675453423"
                style={deliveryType === 'emporter' ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
              />
              {errors.telephone && <span className="delivery-form-error">{errors.telephone}</span>}
            </div>

            {/* Email */}
            <div className="delivery-form-group">
              <label className="delivery-form-label">
                <Mail size={16} />
                Email <span className="delivery-required">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={deliveryType === 'emporter'}
                className={`delivery-form-input ${errors.email ? 'delivery-form-input-error' : ''}`}
                placeholder="Ex: jean.dupont@email.com"
                style={deliveryType === 'emporter' ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
              />
              {errors.email && <span className="delivery-form-error">{errors.email}</span>}
            </div>

            {/* Adresse */}
            <div className="delivery-form-group">
              <label className="delivery-form-label">
                <Navigation size={16} />
                Adresse <span className="delivery-required">*</span>
              </label>
              <input
                type="text"
                name="adresse"
                value={formData.adresse}
                onChange={handleChange}
                disabled={deliveryType === 'emporter'}
                className={`delivery-form-input ${errors.adresse ? 'delivery-form-input-error' : ''}`}
                placeholder="Ex: Rue de la Paix, Akwa"
                style={deliveryType === 'emporter' ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
              />
              {errors.adresse && <span className="delivery-form-error">{errors.adresse}</span>}
            </div>

            {/* Complément d'adresse */}
            <div className="delivery-form-group">
              <label className="delivery-form-label">
                <Home size={16} />
                Complément d&apos;adresse
              </label>
              <input
                type="text"
                name="complement"
                value={formData.complement}
                onChange={handleChange}
                className="delivery-form-input"
                placeholder="Ex: Appartement 3B, Bâtiment C"
              />
            </div>

            {/* Ville et Code postal */}
            <div className="delivery-form-row">
              <div className="delivery-form-group delivery-form-group-half">
                <label className="delivery-form-label">
                  Ville <span className="delivery-required">*</span>
                </label>
                <input
                  type="text"
                  name="ville"
                  value={formData.ville}
                  onChange={handleChange}
                  disabled={deliveryType === 'emporter'}
                  className={`delivery-form-input ${errors.ville ? 'delivery-form-input-error' : ''}`}
                  placeholder="Ex: Douala"
                  style={deliveryType === 'emporter' ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                />
                {errors.ville && <span className="delivery-form-error">{errors.ville}</span>}
              </div>

              <div className="delivery-form-group delivery-form-group-half">
                <label className="delivery-form-label">
                  Code postal
                </label>
                <input
                  type="text"
                  name="code_postal"
                  value={formData.code_postal}
                  onChange={handleChange}
                  disabled={deliveryType === 'emporter'}
                  className="delivery-form-input"
                  placeholder="Ex: BP-1234"
                  style={deliveryType === 'emporter' ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                />
              </div>
            </div>

            {/* Instructions supplémentaires */}
            <div className="delivery-form-group">
              <label className="delivery-form-label">
                Instructions supplémentaires
              </label>
              <textarea
                name="autre"
                value={formData.autre}
                onChange={handleChange}
                className="delivery-form-textarea"
                placeholder="Ex: Sonner deux fois, laisser devant la porte..."
                rows="3"
              />
            </div>

            {/* Footer */}
            <div className="delivery-modal-footer">
              <button
                type="button"
                className="delivery-btn delivery-btn-secondary"
                onClick={() => {
                  handleReset();
                  onClose();
                }}
                disabled={isSubmitting}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="delivery-btn delivery-btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="delivery-spinner" />
                    Validation...
                  </>
                ) : (
                  'Suivant'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

DeliveryAddress.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object
};

export default DeliveryAddress;
